import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useSearchParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {login} from '../core/_requests'
import {loadCurrentUser} from '../core/currentUser'
import {setActingBranch} from '../../common/actingBranch'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {brandLogo} from '../../../tenant/applyBranding'
import {refreshTenantContextToken} from '../../../tenant/contextToken'
import {useAuth} from '../core/Auth'
import {useIntl} from 'react-intl'
import {useLang} from '../../../../_metronic/i18n/Metronici18n'
import {authPublicLinks} from '../authLinks'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const {formatMessage} = useIntl()
  const lang = useLang()
  const [loading, setLoading] = useState(false)
  const [passwordVisible, setPasswordVisible] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const [isFormValid, setIsFormValid] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [searchParams] = useSearchParams()
  const passwordWasReset = searchParams.get('reset') === '1'
  const links = authPublicLinks(lang)

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)

      try {
        setIsFormValid(true)
        // Mart v2: a branch picked by whoever signed in here before is not this account's.
        setActingBranch(null)
        const {data: auth} = await login(values.email, values.password, lang)
        saveAuth(auth)
        // Multi-tenant (D6): mint the X-Tenant-Context token now that we have a session, best-effort —
        // a mint failure must never block login.
        refreshTenantContextToken().catch(() => {})
        const data = await loadCurrentUser(auth.token)
        setCurrentUser({
          name: data.name,
          email: data.email,
          token: auth.token,
          role: data.role,
          permissions: data.permissions,
          // The partner shop this account runs (null for an ordinary supplier). Without it a shop
          // account looks ordinary until the next reload: national quantities, the POs menu.
          shop: data.shop ?? null,
          // Mart v2: the supplier's shops and the branch this account works for.
          shops: data.shops ?? [],
          supplier_branch_id: data.supplier_branch_id ?? null,
          acting_branch_id: data.acting_branch_id ?? null,
          auth,
        })
      } catch (error: any) {
        setIsFormValid(false)
        setErrorMessage(error.response?.data?.message || formatMessage({id: 'login_failed'}))
        saveAuth(undefined)
        setStatus(formatMessage({id: 'login_failed'}))
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div>
        <img
          alt=''
          width='170'
          className='mb-10 mb-10 mx-auto d-block'
          src={`${brandLogo(
            lang === 'ar',
            toAbsoluteUrl('/media/logos/logo_revamp' + (lang === 'ar' ? '-ar' : '') + '.svg')
          )}`}
        />
      </div>
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>{formatMessage({id: 'AUTH.LOGIN.BUTTON'})}</h1>
      </div>
      {/* begin::Heading */}

      {passwordWasReset && (
        <div className='mb-lg-10 alert alert-success'>
          <div className='alert-text font-weight-bold'>
            {formatMessage({id: 'AUTH.RESET.SUCCESS'})}
          </div>
        </div>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>
          {formatMessage({id: 'email'})}
        </label>
        <input
          placeholder='Email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': (formik.touched.email && formik.errors.email) || !isFormValid},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
          dir='ltr'
          style={{textAlign: 'left'}}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.email}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>
          {formatMessage({id: 'AUTH.INPUT.PASSWORD'})}
        </label>
        <div className='position-relative'>
          <input
            type={passwordVisible ? 'text' : 'password'}
            autoComplete='current-password'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': (formik.touched.password && formik.errors.password) || !isFormValid,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
            dir='ltr'
            style={{paddingInlineEnd: '3.5rem', backgroundImage: 'none', textAlign: 'left'}}
          />
          <button
            type='button'
            className='btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y me-2'
            onClick={() => setPasswordVisible((visible) => !visible)}
            aria-label={formatMessage({
              id: passwordVisible ? 'AUTH.PASSWORD.HIDE' : 'AUTH.PASSWORD.SHOW',
            })}
            aria-pressed={passwordVisible}
          >
            <i
              className={`fas ${passwordVisible ? 'fa-eye-slash' : 'fa-eye'}`}
              aria-hidden='true'
            />
          </button>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.password}</span>
          </div>
        )}
        {!isFormValid && (
          <div className='fv-plugins-message-container'>
            <span role='alert' className='text-danger'>
              {errorMessage}
            </span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Wrapper */}
      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div />

        {/* begin::Link */}
        <Link to='/auth/forgot-password' className='link-primary'>
          {formatMessage({id: 'forgot_pass'})}
        </Link>
        {/* end::Link */}
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className='d-grid mb-3'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>{formatMessage({id: 'continue'})}</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              {formatMessage({id: 'please_wait'})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
      <p>
        {formatMessage({id: 'AUTH.LOGIN.AGREEMENT_PREFIX'})}{' '}
        <a href={links.terms} target='_blank' rel='noreferrer' className='link-primary'>
          {formatMessage({id: 'AUTH.FOOTER.TERMS_AND_CONDITIONS'})}
        </a>
      </p>
    </form>
  )
}
